import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import Nav from "@/components/welp/Nav";
import PageBg, { POSTER_P2_SEARCH } from "@/components/welp/PageBg";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function SearchPage() {
  const { user, loading } = useAuth();
  const [username, setUsername] = useState("");
  const [platform, setPlatform] = useState("");
  const [platforms, setPlatforms] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/search", { replace: true }); return; }
    api.platforms().then(d => setPlatforms(d.platforms)).catch(() => {});
  }, [user, loading, nav]);

  if (loading) return <PageBg><Nav /><div className="max-w-2xl mx-auto px-6 py-20 text-center text-[#3A3A3A] font-semibold">Checking your session…</div></PageBg>;
  if (!user) return null;

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!username.trim() || !platform) return toast.error("Pick a username and platform");
    api.evt("search_started");
    setBusy(true); setResult(null);
    try {
      const r = await api.searchProfile(username.trim(), platform);
      if (!r.exists) setResult({ none: true });
      else setResult(r.profile);
    } catch (err) { toast.error("Search failed"); }
    finally { setBusy(false); }
  };

  return (
    <PageBg poster={POSTER_P2_SEARCH} scrim="bg-white/80">
      <Nav />
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="text-veil">
        <div className="text-xs uppercase tracking-[0.32em] mb-4">
          <span className="text-[#10B981]">Welp</span> <span className="text-[#5A5A5A]">·</span> <span className="text-[#FF1A82]">Dating With Integrity</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-[#1A1A1A]">Look someone <span className="italic text-[#10B981]">up.</span></h1>
        <p className="text-[#3A3A3A] mt-4">Profiles only exist once a review has been submitted. No empty profiles.</p>
        </div>

        <form onSubmit={doSearch} className="mt-12 space-y-4">
          <div className="flex flex-col md:flex-row gap-3">
            <input
              data-testid="search-username"
              value={username} onChange={e => setUsername(e.target.value)}
              placeholder="Username or display name"
              className="flex-1 bg-white/60 border border-black/10 rounded-full px-6 py-4 text-[#1A1A1A] placeholder:text-[#5A5A5A] outline-none focus:border-[#FF1A82] focus:ring-2 focus:ring-[#FF1A82]/30"
            />
            <select
              data-testid="search-platform"
              value={platform} onChange={e => setPlatform(e.target.value)}
              className="md:w-64 bg-white/60 border border-black/10 rounded-full px-6 py-4 text-[#1A1A1A] outline-none focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/30"
            >
              <option value="" className="bg-white/70 backdrop-blur-lg shadow-lg">Platform…</option>
              {platforms.map(p => <option key={p} value={p} className="bg-white/70 backdrop-blur-lg shadow-lg">{p}</option>)}
            </select>
          </div>
          <button disabled={busy} data-testid="search-submit" className="px-7 py-4 rounded-full bg-[#10B981] text-white font-medium glow-pink inline-flex items-center gap-2 hover:brightness-110 transition disabled:opacity-60">
            <Search className="w-4 h-4" /> {busy ? "Searching…" : "Search"}
          </button>
        </form>

        {result?.none && (
          <div className="mt-10 rounded-2xl border border-black/10 bg-black/5 p-8 animate-fade-up">
            <div className="text-xs uppercase tracking-[0.22em] text-[#FF1A82] mb-2">No profile yet</div>
            <h3 className="font-heading text-3xl text-[#1A1A1A]">Be the first to share an experience.</h3>
            <p className="text-[#3A3A3A] mt-3 max-w-md">Profiles only exist once a review has been submitted. You can create this profile by submitting the first review.</p>
            <button onClick={() => nav(`/review/new?u=${encodeURIComponent(username)}&p=${encodeURIComponent(platform)}`)} className="mt-6 px-6 py-3 rounded-full bg-[#FF1A82] text-white font-semibold glow-lime hover:brightness-110 transition" data-testid="search-create-review">
              Submit first review
            </button>
          </div>
        )}

        {result && !result.none && (
          <div className="mt-10 rounded-3xl border border-[#10B981]/40 bg-black/5 p-8 glow-pink animate-fade-up" data-testid="snapshot-card">
            <div className="text-xs uppercase tracking-[0.32em] text-[#FF1A82] mb-2">Snapshot</div>
            <h3 className="font-heading text-4xl text-[#1A1A1A]">{result.username}</h3>
            <div className="text-[#3A3A3A] mt-1">{result.platform}</div>
            <div className="flex items-end gap-3 mt-6">
              <div className="text-5xl font-light text-[#10B981] text-glow-pink tabular-nums">{result.review_count}</div>
              <div className="text-[#3A3A3A] mb-2">reviews on file</div>
            </div>
            {result.owner_story?.story && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 text-lg font-bold text-[#10B981]" data-testid="story-teaser">
                {result.story_love_count > 0
                  ? <>❤️ {result.story_love_count} loved their story</>
                  : <>📖 They've shared their story</>}
              </div>
            )}
            <button onClick={() => nav(`/profile/${result.profile_id}`)} className="mt-8 px-7 py-4 rounded-full bg-[#FF1A82] text-white font-semibold glow-lime hover:brightness-110 transition" data-testid="view-skinny-btn">
              View The Skinny →
            </button>
          </div>
        )}
      </section>
    </PageBg>
  );
}
